import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Eye, EyeOff, Check, ChevronDown } from 'lucide-react';
import Logo from '../components/Logo';

const GCC_CODES = ['AE', 'SA', 'QA', 'OM', 'BH', 'KW'];

const REGION_MAP = {
  IN: { flag: '🇮🇳', name: 'India',   currency: 'INR', tax: 'GST' },
  AE: { flag: '🇦🇪', name: 'UAE',     currency: 'AED', tax: 'VAT' },
  SA: { flag: '🇸🇦', name: 'Saudi',   currency: 'SAR', tax: 'VAT' },
  QA: { flag: '🇶🇦', name: 'Qatar',   currency: 'QAR', tax: 'VAT' },
  OM: { flag: '🇴🇲', name: 'Oman',    currency: 'OMR', tax: 'VAT' },
  BH: { flag: '🇧🇭', name: 'Bahrain', currency: 'BHD', tax: 'VAT' },
  KW: { flag: '🇰🇼', name: 'Kuwait',  currency: 'KWD', tax: 'VAT' },
};

export default function Signup() {
  const navigate = useNavigate();
  const { signup: authSignup, applyAuth } = useAuth();
  const [params] = useSearchParams();
  const wantsBasic = params.get('plan') === 'basic';
  const wantsTrial = !wantsBasic && (params.get('trial') === 'premium' || !params.get('plan'));
  const preferredCountry = (params.get('country') || '').toUpperCase() || null;

  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [state, setState] = useState('');
  const [states, setStates] = useState([]);
  const [showStates, setShowStates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasInvite, setHasInvite] = useState(false);
  const [inviteCode, setInviteCode] = useState('');

  const regionInfo = useMemo(() => {
    const code = preferredCountry && REGION_MAP[preferredCountry] ? preferredCountry : 'IN';
    return { code, ...REGION_MAP[code] };
  }, [preferredCountry]);

  useEffect(() => {
    if (regionInfo.code !== 'IN') { setStates([]); setState(''); return; }
    api.states().then(r => setStates(r.states || [])).catch(() => {});
  }, [regionInfo.code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { setError('Name, email and password are required'); return; }
    if (!hasInvite && !businessName) { setError('Business name is required'); return; }
    if (hasInvite && !inviteCode.trim()) { setError('Enter your invite code'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError('');
    setLoading(true);
    try {
      if (hasInvite) {
        const r = await api.signupWithInvite({
          name: name.trim(), email: email.trim(), password,
          invite_code: inviteCode.trim().toUpperCase(),
        });
        applyAuth(r);
        navigate('/home', { replace: true });
        return;
      }
      await authSignup({
        name: name.trim(), business_name: businessName.trim(),
        email: email.trim(), password, state: state || null,
        trial: wantsTrial ? 'premium' : null,
        plan: wantsBasic ? 'basic' : null,
        country_code: regionInfo.code,
      });
      // Phase 20b — if the landing page passed a country preference, apply
      // it now via a post-signup PATCH. Best-effort; failure never blocks.
      if (preferredCountry && GCC_CODES.includes(preferredCountry)) {
        try {
          const token = localStorage.getItem('auth_token');
          if (token) {
            await api.regionPatch(token, { country_code: preferredCountry });
          }
        } catch { /* silent — non-blocking */ }
      }
      navigate('/home', { replace: true });
    } catch (err) {
      setError(err?.message ?? 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-light-gradient flex items-center justify-center p-6" data-testid="signup-page">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex flex-col items-center mb-6">
          <Logo size="xl" theme="light" className="mb-6" />
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Create your workspace</h1>
          <p className="text-gray-500 mt-2 text-center">Set up your business on AI Business Intelligence Manager in seconds.</p>
        </div>

        {/* Trial badge */}
        {wantsTrial && (
          <div className="mx-auto w-fit flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-brand-teal to-brand-teal-deep text-white text-sm font-bold shadow-teal mb-6" data-testid="signup-trial-pill">
            <Check size={14} strokeWidth={2.6} />
            14-day full Premium trial · No credit card
          </div>
        )}

        {/* Region badge */}
        <Link
          to={`/?country=${regionInfo.code}`}
          className="mx-auto w-fit flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm text-sm font-semibold text-gray-700 hover:border-brand-purple/40 hover:shadow-md transition-all mb-6"
          data-testid="signup-region-badge"
          title="Click to change region"
        >
          <span className="text-base">{regionInfo.flag}</span>
          <span>Region: {regionInfo.name} · {regionInfo.currency} · {regionInfo.tax}</span>
        </Link>

        {/* Form */}
        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="signup-error">
              {error}
            </div>
          )}

          {/* Invite toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-white border border-gray-200">
            <div>
              <p className="text-gray-900 font-semibold text-sm">Joining a team</p>
              <p className="text-gray-400 text-xs mt-0.5">Toggle on if you have an invite code</p>
            </div>
            <button
              type="button"
              onClick={() => setHasInvite(v => !v)}
              data-testid="invite-toggle"
              className={`w-12 h-7 rounded-full transition-colors relative ${hasInvite ? 'bg-brand-purple' : 'bg-gray-200'}`}
              role="switch"
              aria-checked={hasInvite}
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-1 transition-transform ${hasInvite ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {hasInvite && (
            <div>
              <label htmlFor="invite-code" className="block text-gray-900 text-sm font-bold mb-2">Invite code</label>
              <input id="invite-code" data-testid="signup-invite-code" className="w-full h-14 px-4 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-brand-purple outline-none uppercase" placeholder="e.g. ABC23XYZ" value={inviteCode} onChange={e => setInviteCode(e.target.value)} />
            </div>
          )}

          <div>
            <label htmlFor="signup-name" className="block text-gray-900 text-sm font-bold mb-2">Your name</label>
            <input id="signup-name" data-testid="signup-name" className="w-full h-14 px-4 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-brand-purple outline-none" placeholder="Ravi Menon" value={name} onChange={e => setName(e.target.value)} />
          </div>

          <div>
            <label htmlFor="signup-business" className="block text-gray-900 text-sm font-bold mb-2">Business name</label>
            <input id="signup-business" data-testid="signup-business" className={`w-full h-14 px-4 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-brand-purple outline-none ${hasInvite ? 'opacity-50' : ''}`} placeholder={hasInvite ? '(comes from invite)' : 'Ravi Traders'} value={businessName} onChange={e => setBusinessName(e.target.value)} disabled={hasInvite} />
          </div>

          <div>
            <label htmlFor="signup-email" className="block text-gray-900 text-sm font-bold mb-2">Email</label>
            <input id="signup-email" data-testid="signup-email" type="email" className="w-full h-14 px-4 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-brand-purple outline-none" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} />
          </div>

          <div>
            <label htmlFor="signup-password" className="block text-gray-900 text-sm font-bold mb-2">Password</label>
            <div className="relative">
              <input id="signup-password" data-testid="signup-password" type={showPass ? 'text' : 'password'} className="w-full h-14 px-4 pr-12 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-brand-purple outline-none" placeholder="At least 6 characters" value={password} onChange={e => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShowPass(v => !v)} data-testid="signup-toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600" aria-label={showPass ? 'Hide' : 'Show'}>
                {showPass ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {regionInfo.code === 'IN' && (
          <div className="relative">
            <label htmlFor="signup-state" className="block text-gray-900 text-sm font-bold mb-2">State (optional)</label>
            <button
              type="button"
              onClick={() => !hasInvite && setShowStates(v => !v)}
              data-testid="state-picker"
              className={`w-full h-14 px-4 rounded-xl border border-gray-200 bg-white text-left flex items-center justify-between ${hasInvite ? 'opacity-50' : ''}`}
              disabled={hasInvite}
            >
              <span className={state ? 'text-gray-900' : 'text-gray-400'}>{state || 'Select an Indian state'}</span>
              <ChevronDown size={16} className="text-gray-400" />
            </button>
            {showStates && (
              <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-xl bg-white border border-gray-200 shadow-lg">
                {states.map(s => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`state-${s}`}
                    onClick={() => { setState(s); setShowStates(false); }}
                    className="w-full px-4 py-3 text-left text-sm text-gray-900 hover:bg-brand-purple/5 flex items-center justify-between border-b border-gray-100 last:border-0"
                  >
                    {s}
                    {state === s && <Check size={16} className="text-brand-purple" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="signup-submit-btn"
            className="w-full h-14 rounded-xl bg-gradient-to-r from-brand-purple via-brand-purple-soft to-purple-700 text-white font-bold text-base shadow-accent hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /> : 'Create workspace'}
          </button>
        </form>

        <p className="text-center mt-6 text-gray-500 text-sm">
          Already have a workspace?{' '}
          <Link to="/login" className="text-brand-purple font-bold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
